import React from 'react';
import { Hero } from '../components/Hero';
import { TestimonialCard } from '../components/TestimonialCard';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, Heart, Plane, Home, Cross } from 'lucide-react';
export function Trips() {
  const upcomingTrips = [{
    destination: 'Uganda',
    season: 'Fall 2025',
    timing: 'August 29 - September 6, 2025',
    duration: '9 Days',
    focus: 'Water wells, church support, medical outreach, Gospel sharing',
    difficulty: 'Moderate',
    accommodations: 'Basic guest houses and local lodging',
    cost: '$2,000 - Fundraising options available!',
    highlights: ['Drill and dedicate new water wells', 'Install solar systems at churches', 'Medical outreach in rural villages', 'Worship and evangelism with local churches', 'Visit previous project sites'],
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=800'
  }, {
    destination: 'Philippines',
    season: 'Spring 2026',
    timing: 'Upcoming',
    duration: '12 Days',
    focus: 'School support, community development, church partnerships, Gospel outreach',
    difficulty: 'Moderate to Challenging',
    accommodations: 'Simple hotels and mission housing',
    cost: 'TBD - Fundraising options available!',
    highlights: ['Install solar systems at schools', 'Deliver books and educational resources', 'Partner with local churches for outreach', 'Share the Gospel in communities', 'Experience Filipino culture and hospitality'],
    image: 'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=800'
  }];
  const testimonials = [{
    quote: 'I expected a trip. I found my calling. Uganda changed everything about how I see God, myself, and the world.',
    author: 'Sarah Mitchell',
    role: 'Uganda 2023'
  }, {
    quote: 'This changed my relationship with God forever. I saw Him move in ways I never imagined possible.',
    author: 'David Chen',
    role: 'Philippines 2023'
  }, {
    quote: 'I will never see the world the same. The people we served taught me more about faith than I taught them.',
    author: 'Emily Rodriguez',
    role: 'Uganda 2024'
  }];
  const expectations = [{
    icon: <Cross size={32} data-id="element-375" />,
    title: 'Gospel-Centered Mission',
    description: 'Every trip is rooted in the Great Commission. We share Christ through word and deed, partnering with local churches to spread the Gospel.'
  }, {
    icon: <Heart size={32} data-id="element-376" />,
    title: 'Hands-On Service',
    description: "You'll work alongside communities—drilling wells, installing solar systems, teaching, and serving in practical, tangible ways."
  }, {
    icon: <Users size={32} data-id="element-377" />,
    title: 'Cultural Immersion',
    description: 'Experience life in rural villages. Eat local food. Learn local customs. Build genuine relationships that transcend language and culture.'
  }, {
    icon: <Home size={32} data-id="element-378" />,
    title: 'Basic Accommodations',
    description: 'Expect simple lodging, shared facilities, and basic meals. Comfort is secondary to mission. This is about service, not tourism.'
  }, {
    icon: <Plane size={32} data-id="element-379" />,
    title: 'Physical Demands',
    description: "Trips involve walking, lifting, heat, and physical work. Moderate fitness required. We'll prepare you for what to expect."
  }, {
    icon: <MapPin size={32} data-id="element-380" />,
    title: 'Safety & Support',
    description: 'Experienced team leaders guide every trip. We prioritize safety, provide orientation, and support you throughout the journey.'
  }];
  return <div className="pt-20" data-id="element-381">
      <Hero title="Go With Big Love" subtitle="Mission trips that transform communities—and transform you." backgroundImage="https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=1600" height="large" data-id="element-382" />

      {/* Why We Go */}
      <section className="py-20 bg-white" data-id="element-383">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-384">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-385">
            <h2 className="text-[var(--color-text)] mb-8" data-id="element-386">Why We Go</h2>
            <div className="space-y-6 text-lg text-[var(--color-text-light)] leading-relaxed" data-id="element-387">
              <p data-id="element-388">For the Great Commission—to make disciples of all nations.</p>
              <p data-id="element-389">
                For communities who need to know they are seen, loved, and
                valued by God.
              </p>
              <p data-id="element-390">
                For eternal impact that reaches beyond what we can see or
                measure.
              </p>
              <p className="text-2xl font-bold text-[var(--color-primary)] pt-4" data-id="element-391">
                We go because love is not love until you give it away.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What To Expect */}
      <section className="py-20 bg-[var(--color-gray-light)]" data-id="element-392">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-393">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-394">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-395">What To Expect</h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-3xl mx-auto" data-id="element-396">
              Big Love trips are mission-focused, physically demanding, and
              spiritually transformative. Here's what you need to know.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-397">
            {expectations.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="bg-white rounded-lg p-8 shadow-lg" data-id="element-398">
                <div className="text-[var(--color-primary)] mb-4" data-id="element-399">
                  {item.icon}
                </div>
                <h3 className="text-lg text-[var(--color-text)] mb-3 font-bold" data-id="element-400">
                  {item.title}
                </h3>
                <p className="text-[var(--color-text-light)] leading-relaxed" data-id="element-401">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Upcoming Trips */}
      <section className="py-20 bg-white" data-id="element-402">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-403">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-404">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-405">Upcoming Trips</h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-406">
              Join us on a journey that will change your life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-407">
            {upcomingTrips.map((trip, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="bg-white rounded-lg overflow-hidden shadow-xl" data-id="element-408">
                <div className="h-64 overflow-hidden" data-id="element-409">
                  <img src={trip.image} alt={trip.destination} className="w-full h-full object-cover" data-id="element-410" />
                </div>
                <div className="p-8" data-id="element-411">
                  <div className="flex items-center justify-between mb-4" data-id="element-412">
                    <h3 className="text-3xl text-[var(--color-text)] font-bold" data-id="element-413">
                      {trip.destination}
                    </h3>
                    <span className="text-[var(--color-primary)] font-semibold text-lg" data-id="element-414">
                      {trip.season}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6 text-[var(--color-text-light)]" data-id="element-415">
                    <p data-id="element-416">
                      <strong className="text-[var(--color-text)]" data-id="element-417">
                        Timing:
                      </strong>{' '}
                      {trip.timing}
                    </p>
                    <p data-id="element-418">
                      <strong className="text-[var(--color-text)]" data-id="element-419">
                        Duration:
                      </strong>{' '}
                      {trip.duration}
                    </p>
                    <p data-id="element-420">
                      <strong className="text-[var(--color-text)]" data-id="element-421">
                        Cost:
                      </strong>{' '}
                      {trip.cost}
                    </p>
                    <p data-id="element-422">
                      <strong className="text-[var(--color-text)]" data-id="element-423">
                        Focus:
                      </strong>{' '}
                      {trip.focus}
                    </p>
                    <p data-id="element-424">
                      <strong className="text-[var(--color-text)]" data-id="element-425">
                        Physical Difficulty:
                      </strong>{' '}
                      {trip.difficulty}
                    </p>
                    <p data-id="element-426">
                      <strong className="text-[var(--color-text)]" data-id="element-427">
                        Accommodations:
                      </strong>{' '}
                      {trip.accommodations}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-428">
                    <h4 className="text-[var(--color-text)] font-semibold mb-3" data-id="element-429">
                      Trip Highlights:
                    </h4>
                    <ul className="space-y-2" data-id="element-430">
                      {trip.highlights.map((highlight, idx) => <li key={idx} className="flex items-start text-[var(--color-text-light)]" data-id="element-431">
                          <span className="text-[var(--color-primary)] mr-2" data-id="element-432">
                            •
                          </span>
                          {highlight}
                        </li>)}
                    </ul>
                  </div>

                  <Button variant="primary" className="w-full" data-id="element-433">
                    Learn More & Apply
                  </Button>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[var(--color-gray-light)]" data-id="element-434">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-435">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-436">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-437">
              Experiences From The Field
            </h2>
            <p className="text-lg text-[var(--color-text-light)]" data-id="element-438">
              Hear from those who have experienced Big Love trips.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-439">
            {testimonials.map((testimonial, index) => <TestimonialCard key={index} {...testimonial} delay={index * 0.1} data-id="element-440" />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[var(--color-primary)] text-white" data-id="element-441">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-442">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-443">
            <h2 className="text-white mb-6" data-id="element-444">Ready To Go?</h2>
            <p className="text-lg md:text-xl text-white/95 mb-8 leading-relaxed" data-id="element-445">
              Your life will never be the same. Apply today and join us on a
              journey of faith, service, and transformation.
            </p>
            <Button variant="secondary" size="lg" href="/get-involved" data-id="element-446">
              APPLY FOR A TRIP
            </Button>
          </motion.div>
        </div>
      </section>
    </div>;
}